/**
 * Quiz state machine + step sequencer (Step 17.2).
 *
 * Pure module — no React, no I/O, no browser APIs. Drives the
 * StartFlowClient component via a discriminated step union.
 *
 * Two kinds of cards in the flow:
 *   - QuestionStep — counts toward the visible step count + the
 *     progress bar
 *   - InterstitialStep — sits between questions, doesn't count in
 *     the progress bar; sub-state of the previous question
 *
 * Conditional menstrual step: inserted after dob_sex_zip when the
 * user picked sex_at_birth='female'. The step list is derived
 * fresh on each call — same input always produces the same list.
 *
 * Track 5.Y-cat-quiz-amplification Phase 1: 4 new question steps
 * (energy / focus / memory / sleep_band) replace the implicit
 * inference of these dimensions from primary_concerns + the legacy
 * numeric sleep slider. The habits step (smoking + alcohol) is
 * removed — those questions move to a post-purchase clarity-AI flow.
 * The 'trust' interstitial after family_history is also dropped per
 * operator decision (cognitive load reduction; small_daily +
 * after_concerns interstitials remain for framing).
 */

import type { HealthProfileInput } from './types';
import type { InterstitialSlot } from './interstitial-content';

export type StepKind = 'question' | 'interstitial';

export type QuestionId =
  | 'concerns'
  | 'energy'
  | 'focus'
  | 'memory'
  | 'sleep_band'
  | 'lifestyle'
  | 'conditions'
  | 'family_history'
  | 'goal'
  | 'email_phone'
  | 'dob_sex_zip'
  | 'menstrual';
// 'habits' (smoking + alcohol) was removed Phase 1; moved to
// post-purchase clarity-AI.
// 'biometrics' was removed in 17.3.5 — see
// src/app/start/_deferred/BiometricsStep.tsx for the preserved
// component + intended future placement.
// 'name' was merged into 'email_phone' on 2026-05-14 (Track 3.10) —
// firstName + lastName now render in the same step as email so the
// visible step count drops from 13 → 12 (female; 11 male). step_id
// 'email_phone' kept for stability of operator-authored
// quiz_step_overrides; copy now reads "Let's set up your account."

export interface QuestionStep {
  kind: 'question';
  id: QuestionId;
  /** 1-based — what the progress bar shows. Conditional steps still
   *  consume a number when present so the bar stays smooth. */
  displayNumber: number;
}

export interface InterstitialStep {
  kind: 'interstitial';
  slot: InterstitialSlot;
  /** The question step this interstitial sits *after*. Drives the
   *  progress bar — interstitials inherit the previous question's
   *  displayNumber so the bar doesn't jiggle. */
  afterQuestion: QuestionId;
  displayNumber: number;
}

export type FlowStep = QuestionStep | InterstitialStep;

/**
 * Build the ordered step list for a given profile state.
 *
 * The menstrual step only appears when the profile has
 * `menstrualStatus !== 'not_applicable'` set OR when sex/gender is
 * known to be female. Block 2 carries sex on the dob_sex_zip step;
 * we read it from the input via a side channel — see `sexAtBirth`
 * on the helper signature below.
 */
export function buildFlow(input: { sexAtBirth?: 'female' | 'male' | null } = {}): FlowStep[] {
  const includeMenstrual = input.sexAtBirth === 'female';

  // Phase 1 question order (Track 3.10 — name merged into email_phone):
  //   concerns → [interstitial: after_concerns ApoB/Lp(a)]
  //     → energy → focus → memory → sleep_band → lifestyle
  //       → [interstitial: after_habits "Small daily choices"]
  //     → conditions → family_history → goal
  //       → [interstitial: processing]
  //     → email_phone (name + email) → dob_sex_zip → (menstrual?)
  const questionOrder: QuestionId[] = [
    'concerns',
    'energy',
    'focus',
    'memory',
    'sleep_band',
    'lifestyle',
    'conditions',
    'family_history',
    'goal',
    'email_phone',
    'dob_sex_zip',
    ...(includeMenstrual ? (['menstrual'] as QuestionId[]) : []),
  ];

  const flow: FlowStep[] = [];
  questionOrder.forEach((id, idx) => {
    const displayNumber = idx + 1;
    flow.push({ kind: 'question', id, displayNumber });

    // Insert interstitials after specific anchor questions. The
    // 'after_habits' slot historically followed the smoking/alcohol
    // step; with that step removed, it now follows lifestyle (last
    // dimension before the medical/family/goal long tail).
    if (id === 'concerns') {
      flow.push({ kind: 'interstitial', slot: 'after_concerns', afterQuestion: id, displayNumber });
    } else if (id === 'lifestyle') {
      flow.push({ kind: 'interstitial', slot: 'after_habits', afterQuestion: id, displayNumber });
    } else if (id === 'goal') {
      flow.push({ kind: 'interstitial', slot: 'processing', afterQuestion: id, displayNumber });
    }
    // 'trust' interstitial (after family_history) dropped Phase 1.
  });

  return flow;
}

/** Total visible step count (questions only) for the progress bar. */
export function totalQuestionCount(flow: FlowStep[]): number {
  return flow.filter((s) => s.kind === 'question').length;
}

/** Find a step by question id; returns the index in `flow`. */
export function indexOfQuestion(flow: FlowStep[], id: QuestionId): number {
  return flow.findIndex((s) => s.kind === 'question' && s.id === id);
}

/** Decide whether the profile has enough data to show a given
 *  question's "Continue" enabled. The orchestrator can use this to
 *  derive button-disabled state without each step component
 *  duplicating the rule. */
export function canAdvanceFrom(question: QuestionId, p: HealthProfileInput): boolean {
  switch (question) {
    case 'concerns':
      return Array.isArray(p.primaryConcerns) && p.primaryConcerns.length > 0;
    case 'energy':
      return Boolean(p.energyPattern);
    case 'focus':
      return Boolean(p.cognitiveState);
    case 'memory':
      return Boolean(p.memoryState);
    case 'sleep_band':
      return Boolean(p.sleepHoursBand);
    case 'lifestyle':
      return Boolean(p.activityLevel && p.dietPattern);
    case 'conditions':
    case 'family_history':
      // Multi-select with "None" option — any selection counts; even
      // empty arrays are valid (user can opt to skip via "None"
      // explicitly choosing none from the list).
      return true;
    case 'goal':
      return (p.primaryGoals ?? []).length > 0;
    case 'email_phone':  // wired in StartFlowClient via lead-form local state
    case 'dob_sex_zip':  //   (Track 3.10: 'email_phone' now also captures
    case 'menstrual':    //    firstName + lastName, gated in gateForStep)
      return true;
  }
}
